import * as THREE from 'three';
import { RebarElement, Element } from './types';
import { RebarShapeManager } from './RebarShapeManager';
import { mergeGeometriesIntoMesh } from '../rendering/instancing/mergeGeometries';
import { computeElementsBBox, bboxSize } from '../services/linked/LinkedProjectService';

export class RebarSolver {

    static solve(rebar: RebarElement, host: Element): THREE.Mesh {
        const layout = rebar.geometryData;
        const shape = RebarShapeManager.getShape(layout.shapeId);

        if (!shape) return new THREE.Mesh();

        // 1. Host dimensions measured from the host's real geometry (column
        // cross-section, wall run/thickness, floor boundary) — never a
        // hard-coded block.
        const hostSize = this.resolveHostSize(host);

        const cover = 0.04; // 40mm default

        // 2. Resolve Single Bar Geometry
        const shapeGeo = RebarShapeManager.resolveShapeGeometry(layout.shapeId, hostSize, cover);
        if (shapeGeo.points.length < 2) return new THREE.Mesh();

        // 3. Apply Layout Rule (Quantity, Spacing) to generate multiple bars
        const geometries: THREE.BufferGeometry[] = [];

        // RebarTool stores the resolved set on geometryData as
        // numberOfBars + distribution.spacing (mm); the legacy RebarElement
        // shape uses quantity/spacing (m). Read both so placed sets render
        // their whole bar family instead of collapsing to a single bar.
        const g = layout as any;
        const count = Math.max(1, g.quantity ?? g.numberOfBars ?? 1);
        const spacingMm = g.distribution?.spacing;
        const spacing = typeof g.spacing === 'number' && g.spacing > 0
            ? g.spacing
            : typeof spacingMm === 'number' && spacingMm > 0
                ? spacingMm / 1000
                : 0.2; // 200mm

        // Distribution Path: e.g. Along Z axis for Stirrups in a Column relative to host origin
        const distributionDir = new THREE.Vector3(0, 0, 1);
        const startOffset = new THREE.Vector3(cover, cover, cover); // Bottom-Left-Corner + Cover

        // Generate Curve for tube
        const curve = new THREE.CatmullRomCurve3(shapeGeo.points, false, 'catmullrom', 0.05);
        const tubeGeo = new THREE.TubeGeometry(curve, 10, layout.barDiameter / 2000, 8, false); // diam is mm, convert to meters radius

        for (let i = 0; i < count; i++) {
            const instanceGeo = tubeGeo.clone();

            // Calc Position
            const pos = startOffset.clone().add(distributionDir.clone().multiplyScalar(i * spacing));

            instanceGeo.translate(pos.x, pos.y, pos.z);
            geometries.push(instanceGeo);
        }

        // 4. Merge via BufferGeometryUtils (task 3.2: resolves former TODO merge)
        return mergeGeometriesIntoMesh(geometries);
    }

    /**
     * Measure the host's true extents (x × y × z) from its geometry data.
     * Typed categories read their canonical fields; anything else falls back
     * to the generic geometry walker over the element payload.
     */
    private static resolveHostSize(host: Element): THREE.Vector3 {
        const g = (host as any).geometryData ?? {};

        if (host.category === 'Structural Columns') {
            const width = Number(host.parameters?.['Width']) || 0.3;
            const depth = Number(host.parameters?.['Depth']) || 0.3;
            const height = Number(g.height) || 3.0;
            return new THREE.Vector3(width, depth, height);
        }

        const [sx, sy, sz] = bboxSize(computeElementsBBox([[host.id, host as any]]));
        return new THREE.Vector3(sx, sy, Math.max(sz, Number(g.height) || 0));
    }
}
